// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Tab suggestions: where to bridge each board to whatever surrounds it, and
 * where a tab alone will not do.
 *
 * Each side of a board's bounding box is probed with rays cast outward at a
 * fixed step. A ray first finds the board's own outermost face on that line —
 * the farthest crossing of its outline, so a notch is followed rather than
 * bridged over — and then the nearest face of any other piece. Consecutive
 * probes that reach the same piece across a similar gap make one stretch, and
 * tabs are spread along each stretch by the requested spacing.
 *
 * Every candidate is checked with the same `previewTabs` the canvas uses, so a
 * suggestion is never a tab the preview would then reject. A candidate that
 * fails is moved along the side before it is given up.
 *
 * The same pass reports what it could not do well: gaps no tab fits, gaps so
 * wide the tabs are long and weak, pieces beyond `tab_max_length`, and boards
 * held from one side only. For the wide and unreachable gaps it proposes a
 * rect rail that closes the gap down to the panel's usual width.
 */

import type { Point } from '../model/geometry.ts';
import type { PanelPreview } from './preview.ts';
import { polygonGap } from './clearance.ts';
import { type Polygon, ringsOf } from './outline.ts';
import type { DefaultsSpec, RailEntry, TabEntry } from './spec.ts';
import { previewTabs } from './tabs.ts';

/** Distance between probes along a side, in millimetres. */
const STEP = 0.5;
/** Probes whose gaps differ by more than this belong to different stretches. */
const GAP_JUMP = 0.25;
/** A stretch longer than this gets at least two tabs, so the board cannot pivot. */
const TWO_TAB_LENGTH = 30;
/** How far a probe starts inside the board, and steps off its face. */
const INSET = 1;
const NUDGE = 0.01;
/** A tab longer than this many tab widths is weak enough to be worth a rail. */
const LONG_TAB = 2;
/** The gap a suggested rail leaves when the panel has none to copy. */
const FALLBACK_GAP = 3;
/** How far a suggested rail reaches into the rail or frame it joins, so the two fuse. */
const FUSE = 1;
/** A suggested rail thinner than this is not worth adding. */
const MIN_RAIL = 1;

export interface AutoTabOptions {
  /** Roughly one tab per this many millimetres of shared edge. */
  spacing: number;
  defaults?: DefaultsSpec;
  /** Tabs to keep. A gap that already has one of these gets no more. */
  existing?: TabEntry[];
}

export interface TabFinding {
  kind: 'no-fit' | 'long' | 'out-of-reach' | 'unheld';
  message: string;
  /** A rect rail that would close the gap. */
  rail?: RailEntry;
  /** The placed tabs this finding is about, which the rail would make redundant. */
  tabs?: TabEntry[];
}

export interface AutoTabResult {
  tabs: TabEntry[];
  findings: TabFinding[];
}

interface Piece {
  poly: Polygon;
  /** `board:<i>`, `rail:<i>` or `patch:<i>`: which spec entry this solid is. */
  owner: string;
}

interface Side {
  name: string;
  /** Outward normal. */
  d: Point;
  opposite: string;
}

const SIDES: Side[] = [
  { name: 'top', d: [0, -1], opposite: 'bottom' },
  { name: 'bottom', d: [0, 1], opposite: 'top' },
  { name: 'left', d: [-1, 0], opposite: 'right' },
  { name: 'right', d: [1, 0], opposite: 'left' },
];

interface Probe {
  /** Position along the side. */
  t: number;
  /** The middle of the gap. */
  origin: Point;
  /** Where the gap starts on the board, and ends on the other piece. */
  face: Point;
  far: Point;
  gap: number;
  owner: string;
}

interface Stretch {
  board: PanelPreview['boards'][number];
  side: Side;
  probes: Probe[];
  /** The widest gap along it: a tab has to bridge that. */
  gap: number;
  owner: string;
}

/** Distances along the ray to every crossing of `ring`. */
function rayHits(from: Point, d: Point, ring: Point[], out: number[]): void {
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    const ex = b[0] - a[0];
    const ey = b[1] - a[1];
    const den = d[0] * ey - d[1] * ex;
    if (Math.abs(den) < 1e-12) continue;
    const ox = a[0] - from[0];
    const oy = a[1] - from[1];
    const s = (ox * ey - oy * ex) / den;
    const t = (ox * d[1] - oy * d[0]) / den;
    if (s >= 0 && t >= 0 && t <= 1) out.push(s);
  }
}

/** The gap in front of `board` on the line through `start` along `d`, if another piece closes it. */
function probe(board: Polygon[], pieces: Piece[], start: Point, d: Point, t: number): Probe | null {
  const own: number[] = [];
  for (const poly of board) for (const ring of ringsOf(poly)) rayHits(start, d, ring, own);
  if (own.length === 0) return null;
  const faceAt = Math.max(...own);
  const at = (s: number): Point => [start[0] + d[0] * s, start[1] + d[1] * s];
  const from = at(faceAt + NUDGE);

  let best: { distance: number; owner: string } | null = null;
  for (const piece of pieces) {
    const hits: number[] = [];
    for (const ring of ringsOf(piece.poly)) rayHits(from, d, ring, hits);
    for (const s of hits) {
      if (best === null || s < best.distance) best = { distance: s, owner: piece.owner };
    }
  }
  if (best === null) return null;
  return {
    t,
    origin: at(faceAt + NUDGE + best.distance / 2),
    face: at(faceAt),
    far: at(faceAt + NUDGE + best.distance),
    gap: best.distance + NUDGE,
    owner: best.owner,
  };
}

/** Sample indices for `n` tabs spread over `count` probes, kept `margin` probes clear of both ends. */
function spread(count: number, n: number, margin: number): number[] {
  if (n === 1) return [Math.floor((count - 1) / 2)];
  const first = margin;
  const last = count - 1 - margin;
  return Array.from({ length: n }, (_, k) => Math.round(first + ((last - first) * k) / (n - 1)));
}

/** Origins and rails are written to the YAML, so they are kept to a readable 0.1 mm. */
const round = (v: number): number => Math.round(v * 10) / 10;
const mm = (v: number): string => `${round(v)} mm`;

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)]!;
}

/**
 * A rect rail filling `stretch`'s gap, `gap` clear of the board. Against a rail
 * or frame it reaches into it so the two fuse; against another board it floats
 * in the middle of the channel, `gap` clear of both.
 *
 * A rail flush with the board's side can still run along some other board face
 * at its ends — the inside corner of an L, say. Its ends are pulled in by one
 * gap when that happens, and the rail is given up if that is not enough.
 */
function railFor(stretch: Stretch, gap: number, boards: Polygon[]): RailEntry | null {
  const { d } = stretch.side;
  const axis = d[0] === 0 ? 1 : 0;
  const sign = d[axis]!;
  // Positions along the outward normal, growing away from the board.
  const near = Math.max(...stretch.probes.map((p) => p.face[axis]! * sign));
  const far = Math.min(...stretch.probes.map((p) => p.far[axis]! * sign));
  const start = near + gap;
  const end = stretch.owner.startsWith('board:') ? far - gap : far + FUSE;
  if (end - start < MIN_RAIL) return null;

  const across = [start * sign, end * sign].sort((a, b) => a - b) as [number, number];
  const first = stretch.probes[0]!.t - STEP / 2;
  const last = stretch.probes.at(-1)!.t + STEP / 2;

  for (const inset of [0, gap]) {
    const along: [number, number] = [first + inset, last - inset];
    if (along[1] - along[0] < MIN_RAIL) return null;
    const [x0, x1] = axis === 0 ? across : along;
    const [y0, y1] = axis === 0 ? along : across;
    const x = round(x0);
    const y = round(y0);
    const rail: RailEntry = { type: 'rect', at: [x, y], size: [round(x1 - x), round(y1 - y)] };
    const [w, h] = rail.size!;
    const poly: Polygon = { ext: [[x, y], [x + w, y], [x + w, y + h], [x, y + h]], holes: [] };
    // Rounding to 0.1 mm may shave a little off the gap; that is not a conflict.
    if (boards.every((b) => polygonGap(poly, b, gap) >= gap - 0.1)) return rail;
  }
  return null;
}

export function suggestTabs(preview: PanelPreview, options: AutoTabOptions): AutoTabResult {
  const defaults = options.defaults ?? {};
  const width = defaults.tab_width ?? 3;
  const maxLength = defaults.tab_max_length ?? 50;
  const spacing = options.spacing > 0 ? options.spacing : 50;

  const pieces: Piece[] = [];
  for (const b of preview.boards) if (b.error === null) for (const poly of b.polys) pieces.push({ poly, owner: `board:${b.index}` });
  for (const r of preview.rails) if (r.error === null) for (const poly of r.polys) pieces.push({ poly, owner: `rail:${r.index}` });
  for (const p of preview.patches) if (p.error === null) for (const poly of p.polys) pieces.push({ poly, owner: `patch:${p.index}` });
  const solids = pieces.map((p) => p.poly);
  const boards = preview.boards.filter((b) => b.error === null && b.bbox !== null);
  const nameOf = new Map(preview.boards.map((b) => [`board:${b.index}`, b.name]));

  // --- find every gap -----------------------------------------------------------------

  const stretches: Stretch[] = [];
  for (const board of boards) {
    const box = board.bbox!;
    for (const side of SIDES) {
      const vertical = side.d[0] === 0;
      const [lo, hi] = vertical ? [box.minX, box.maxX] : [box.minY, box.maxY];
      // A probe starts just inside the far side of the box, so it crosses the whole board.
      const back = vertical
        ? (side.d[1] < 0 ? box.maxY : box.minY) - side.d[1] * INSET
        : (side.d[0] < 0 ? box.maxX : box.minX) - side.d[0] * INSET;

      let current: Probe[] = [];
      const close = (): void => {
        // Mostly slivers where a side turns a corner; nothing is lost by skipping them.
        if (current.length * STEP >= width) {
          stretches.push({
            board, side, probes: current,
            gap: Math.max(...current.map((p) => p.gap)),
            owner: current[0]!.owner,
          });
        }
        current = [];
      };
      for (let t = lo + STEP / 2; t < hi; t += STEP) {
        const start: Point = vertical ? [t, back] : [back, t];
        const hit = probe(board.polys, pieces, start, side.d, t);
        // A board-to-board gap is seen from both boards; the lower index claims it.
        const mine = hit !== null && !(hit.owner.startsWith('board:') && Number(hit.owner.slice(6)) < board.index);
        const prev = current.at(-1);
        if (!mine || (prev !== undefined && (prev.owner !== hit.owner || Math.abs(prev.gap - hit.gap) > GAP_JUMP))) {
          close();
        }
        if (mine) current.push(hit);
      }
      close();
    }
  }

  // --- place tabs ---------------------------------------------------------------------

  const tabs: TabEntry[] = [];
  const findings: TabFinding[] = [];
  /** Sides that ended up with a tab, per board index. */
  const held = new Map<number, Set<string>>(boards.map((b) => [b.index, new Set<string>()]));
  const hold = (stretch: Stretch): void => {
    held.get(stretch.board.index)?.add(stretch.side.name);
    if (stretch.owner.startsWith('board:')) held.get(Number(stretch.owner.slice(6)))?.add(stretch.side.opposite);
  };
  /** Stretches a rail would help, with the tabs that rail would replace. */
  const wanting: Array<{ stretch: Stretch; finding: TabFinding }> = [];

  const existing = (options.existing ?? []).filter((t) => Array.isArray(t?.origin));
  const taken: Point[] = existing.map((t) => [t.origin[0], t.origin[1]] as Point);
  const clear = (p: Point): boolean => taken.every((q) => Math.hypot(p[0] - q[0], p[1] - q[1]) >= width);

  const fits = (origin: Point, d: Point): TabEntry | null => {
    const tab: TabEntry = { origin: [round(origin[0]), round(origin[1])], direction: [Math.abs(d[0]), Math.abs(d[1])] };
    if (!clear(tab.origin)) return null;
    const [geometry] = previewTabs(solids, [tab], defaults);
    return geometry !== undefined && geometry.error === null && geometry.polys.length === 2 ? tab : null;
  };

  const longFinding = (stretch: Stretch, flagged: TabEntry[]): TabFinding => ({
    kind: 'long',
    message: `${stretch.board.name} ${stretch.side.name}: ` +
      `${flagged.length === 1 ? 'the tab bridges' : `${flagged.length} tabs bridge`} ` +
      `${mm(stretch.gap)} to ${nameOf.get(stretch.owner) ?? 'the rail'}, ` +
      `more than ${LONG_TAB}× the ${mm(width)} tab width`,
    tabs: flagged,
  });

  for (const stretch of stretches) {
    const { probes, side } = stretch;
    const where = `${stretch.board.name} ${side.name}`;
    const target = nameOf.get(stretch.owner) ?? 'the rail';

    // Only a tab along this side's normal bridges this gap; one crossing it at
    // right angles belongs to a stretch running the other way.
    const kept = existing.filter((tab) => {
      const [dx, dy] = tab.direction;
      const len = Math.hypot(dx, dy);
      return len > 0 && Math.abs(dx * side.d[0] + dy * side.d[1]) / len > 0.99
        && probes.some((p) => Math.hypot(p.origin[0] - tab.origin[0], p.origin[1] - tab.origin[1]) < width);
    });
    if (kept.length > 0) {
      hold(stretch);
      if (stretch.gap > LONG_TAB * width) wanting.push({ stretch, finding: longFinding(stretch, kept) });
      continue;
    }

    if (stretch.gap > maxLength) {
      wanting.push({
        stretch,
        finding: {
          kind: 'out-of-reach',
          message: `${where}: ${target} is ${mm(stretch.gap)} away, beyond tab_max_length (${mm(maxLength)})`,
        },
      });
      continue;
    }

    const length = probes.length * STEP;
    const margin = Math.ceil(width / STEP);
    const room = probes.length - 2 * margin;
    let n = Math.max(length > TWO_TAB_LENGTH ? 2 : 1, Math.round(length / spacing));
    if (room < 1) n = 1;
    const pitch = n > 1 ? Math.max(1, Math.floor((room - 1) / (n - 1))) : probes.length;
    const reach = Math.floor(pitch / 2);

    const placed: TabEntry[] = [];
    for (const index of spread(probes.length, n, margin)) {
      for (let shift = 0; shift <= reach; shift++) {
        const candidates = shift === 0 ? [index] : [index + shift, index - shift];
        const tab = candidates
          .filter((i) => i >= 0 && i < probes.length)
          .map((i) => fits(probes[i]!.origin, side.d))
          .find((t) => t !== null);
        if (tab) {
          placed.push(tab);
          taken.push(tab.origin);
          break;
        }
      }
    }
    tabs.push(...placed);

    if (placed.length === 0) {
      findings.push({ kind: 'no-fit', message: `${where}: no tab fits the ${mm(stretch.gap)} gap to ${target}` });
      continue;
    }
    hold(stretch);
    if (stretch.gap > LONG_TAB * width) wanting.push({ stretch, finding: longFinding(stretch, placed) });
  }

  // --- suggest rails ------------------------------------------------------------------

  // A suggested rail leaves the gap the rest of the panel already uses.
  const usual = median(stretches
    .filter((s) => s.gap <= LONG_TAB * width && s.gap <= maxLength)
    .map((s) => s.gap)) ?? FALLBACK_GAP;
  for (const { stretch, finding } of wanting) {
    const rail = railFor(stretch, usual, boards.flatMap((b) => b.polys));
    if (rail !== null) {
      finding.rail = rail;
      finding.message += '; a rail would close it';
    }
    findings.push(finding);
  }

  // --- boards that can swing ----------------------------------------------------------

  for (const board of boards) {
    const sides = [...held.get(board.index)!];
    if (sides.length === 0) {
      findings.push({ kind: 'unheld', message: `${board.name} is not held by any tab` });
    } else if (sides.length === 1) {
      findings.push({
        kind: 'unheld',
        message: `${board.name} is held on its ${sides[0]} side only and can swing; tab another side too`,
      });
    }
  }

  return { tabs, findings };
}
